import api from '../../../core/api/client';

const BASE = '/content';
const path = (id) => `${BASE}/${encodeURIComponent(id)}`;

const toApiError = (error) => {
  if (error?.response) {
    const d = error.response.data;
    return {
      status: error.response.status,
      message: d?.detail || d?.message || d?.error || error.message || 'Request failed',
      data: d,
    };
  }
  return { status: 0, message: error?.message || 'Unexpected error', data: null };
};

const wrap = async (fn) => {
  try {
    return await fn();
  } catch (error) {
    throw toApiError(error);
  }
};

export const contentService = {
  async list(filters = {}) {
    return wrap(async () => {
      const params = new URLSearchParams();
      if (filters.status) {
        (Array.isArray(filters.status) ? filters.status : [filters.status])
          .forEach(s => params.append('status', s));
      }
      if (filters.assigneeId) params.set('assigneeId', filters.assigneeId);
      if (filters.projectId) params.set('projectId', filters.projectId);
      if (filters.promotionId) params.set('promotionId', filters.promotionId);
      if (filters.accountId) params.set('accountId', filters.accountId);
      const response = await api.get(BASE, { params });
      return response.data;
    });
  },

  async create(data) {
    return wrap(async () => {
      const response = await api.post(BASE, data);
      return response.data;
    });
  },

  async getBacklog() {
    return wrap(async () => {
      const response = await api.get(`${BASE}/backlog`);
      return response.data;
    });
  },

  async getCalendar(params = {}) {
    return wrap(async () => {
      const response = await api.get(`${BASE}/calendar`, { params });
      return response.data;
    });
  },

  async get(itemId) {
    return wrap(async () => {
      const response = await api.get(path(itemId));
      return response.data;
    });
  },

  async update(itemId, updates) {
    return wrap(async () => {
      const response = await api.patch(path(itemId), updates);
      return response.data;
    });
  },

  async delete(itemId) {
    return wrap(async () => {
      const response = await api.delete(path(itemId));
      return response.data;
    });
  },

  async changeStatus(itemId, status) {
    return wrap(async () => {
      const response = await api.patch(`${path(itemId)}/status`, { status });
      return response.data;
    });
  },

  async review(itemId, { action, comment }) {
    return wrap(async () => {
      const response = await api.post(`${path(itemId)}/review`, { action, comment });
      return response.data;
    });
  },

  async addPublishedLink(itemId, linkData) {
    return wrap(async () => {
      const response = await api.post(`${path(itemId)}/published-links`, linkData);
      return response.data;
    });
  },

  async removePublishedLink(itemId, linkId) {
    return wrap(async () => {
      const response = await api.delete(
        `${path(itemId)}/published-links/${encodeURIComponent(linkId)}`
      );
      return response.data;
    });
  },
};

export const PIPELINE_STATUSES = [
  'idea', 'drafting', 'in_design', 'ready', 'scheduled', 'published', 'archived',
];

export const PIPELINE_STATUS_META = {
  idea:      { label: 'Idea',       color: 'bg-gray-100 text-gray-600',   dot: '#9ca3af' },
  drafting:  { label: 'Drafting',   color: 'bg-blue-100 text-blue-700',   dot: '#3b82f6' },
  in_design: { label: 'In Design',  color: 'bg-purple-100 text-purple-700', dot: '#a855f7' },
  ready:     { label: 'Ready',      color: 'bg-teal-100 text-teal-700',   dot: '#14b8a6' },
  scheduled: { label: 'Scheduled',  color: 'bg-yellow-100 text-yellow-700', dot: '#f59e0b' },
  published: { label: 'Published',  color: 'bg-green-100 text-green-700', dot: '#22c55e' },
  archived:  { label: 'Archived',   color: 'bg-gray-100 text-gray-500',   dot: '#d1d5db' },
};
